// Staff invitations (Sahil, 2026-10-20): what a membership's invitation
// state reads as, on the admin's card and to the invited person. Pure, so
// tests/staff-invitations.test.mjs runs the same rules the screens use.
//
// company_members.status: invited | active | declined | cancelled | removed
// | pending (a join request). "Expired" is not stored: an invitation past
// its expires_at (30 days from invited_at/created_at for rows written
// before expires_at existed -- the database's _invite_expires()).

const DAY = 86400000;

// "Nov 3" this year, "Nov 3, 2027" otherwise.
export function shortDate(iso, now = new Date()) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const opts = { month: "short", day: "numeric" };
  if (d.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  return d.toLocaleDateString("en-US", opts);
}

export function inviteExpiresAt(m) {
  if (!m) return null;
  if (m.expires_at) return m.expires_at;
  const from = m.invited_at || m.created_at;
  return from ? new Date(new Date(from).getTime() + 30 * DAY).toISOString() : null;
}

export function isInviteExpired(m, now = new Date()) {
  const at = inviteExpiresAt(m);
  return !!at && new Date(at).getTime() <= now.getTime();
}

// The chip on a team card and the buttons beside it.
//   key: active | invited | expired | declined | cancelled | none
//   actions: which of resend / cancel / invite the card offers
export function inviteChip(m, now = new Date()) {
  const status = m?.status || null;
  if (status === "active") return { key: "active", label: "Active", tone: "positive", actions: [] };
  if (status === "invited") {
    if (isInviteExpired(m, now)) return { key: "expired", label: "Expired", tone: "warn", actions: ["resend"] };
    return { key: "invited", label: "Invited — waiting for them to accept (expires " + shortDate(inviteExpiresAt(m), now) + ")", tone: "info", actions: ["resend", "cancel"] };
  }
  if (status === "declined") return { key: "declined", label: m.declined_at ? "Declined on " + shortDate(m.declined_at, now) : "Declined", tone: "neutral", actions: ["invite_again"] };
  if (status === "cancelled") return { key: "cancelled", label: "Invitation cancelled", tone: "neutral", actions: ["invite_again"] };
  if (status === "pending") return { key: "pending", label: "Asked to join", tone: "info", actions: [] };
  return { key: "none", label: status === "removed" ? "Removed" : "Not invited", tone: "neutral", actions: ["invite"] };
}

// The one confirmation an admin sees before an invitation goes out.
//   hasAccount: the email already has a Housify login (null: unknown)
//   again: resend / invite again rather than a first invitation
export function inviteConfirmText({ name, email, companyName, roleLabel, hasAccount, again = false }) {
  const who = (name ? name + " (" + email + ")" : email);
  const verb = again ? "Send a new invitation to " : "Invite ";
  const head = verb + who + " to " + (companyName || "this company") + " as " + roleLabel + "?";
  const body = hasAccount === true
    ? "They already have a Housify account. They'll get an email from Housify with an Accept invitation button, and see the invitation when they sign in."
    : hasAccount === false
      ? "They don't have a Housify account yet. They'll get an email to create one, then they accept your invitation."
      : "They'll get an email asking them to accept.";
  return head + "\n\n" + body + "\n\nThey get no access until they accept. The invitation expires in 14 days." + (again ? " Any earlier invitation link stops working." : "");
}

// The one line an admin reads after an invitation was saved.
export function inviteResultText({ name, email, result }) {
  const who = name || email;
  const until = result?.expires_at ? " It expires " + shortDate(result.expires_at) + "." : "";
  if (result?.email_sent) {
    const where = result.email_to && result.email_to !== email ? " (test site: delivered to " + result.email_to + ")" : "";
    return { tone: "success", text: "Invitation emailed to " + who + where + ". They'll show as Active once they accept." + until };
  }
  return { tone: "warning", text: "Invitation saved for " + who + ", but no email went out" + (result?.note ? " (" + result.note + ")" : "") + ". They can still accept it from Housify when they sign in." + until };
}

// The emailed link, /invite/<token>, survives the sign-in it may need.
const KEY = "pendingInviteToken";
let memoToken = null;
export function captureInviteToken(loc = typeof window !== "undefined" ? window.location : null, history = typeof window !== "undefined" ? window.history : null) {
  if (!loc || !String(loc.pathname || "").startsWith("/invite/")) return null;
  const token = String(loc.pathname).slice("/invite/".length).split(/[?#/]/)[0];
  if (!/^[A-Za-z0-9_-]{20,200}$/.test(token)) return null;
  memoToken = token;
  try { sessionStorage.setItem(KEY, token); } catch (_e) { /* private mode: the module copy still works this page load */ }
  try { history && history.replaceState(history.state || {}, "", "/"); } catch (_e) { /* the URL stays; harmless */ }
  return token;
}
export function pendingInviteToken() {
  if (memoToken) return memoToken;
  try { return sessionStorage.getItem(KEY) || null; } catch (_e) { return null; }
}
export function clearInviteToken() {
  memoToken = null;
  try { sessionStorage.removeItem(KEY); } catch (_e) { /* nothing stored */ }
}

// A database refusal, said plainly.
export function inviteErrorText(error) {
  const msg = String(error?.message || error || "");
  return msg || "The invitation could not be answered. Please try again.";
}
